/**
 * Roadmap — Wave 2 (Signature Authenticated Routes) executive rewrite.
 *
 * Rebuilt against the LandingV2 aesthetic bar:
 *   • White surface (`--ds-color-surface`) with 1px `--ds-color-border`
 *     hairline + `--ds-shadow-card` for every content card.
 *   • Warm cream page background (`--ds-color-background`) as separator.
 *   • Cards use `--ds-radius-lg`/`xl` max — NO 24px+ blob radii.
 *   • Uppercase eyebrows via `text-[11px] tracking-[0.14em] font-semibold`.
 *   • Display headings use `--ds-font-display` + semibold with
 *     `letter-spacing: -0.02em`.
 *   • H1 stays `--ds-color-ink` — tier signal now surfaces as a `Badge`
 *     beside the title rather than a colored H1.
 *   • Every `--v2-*` variable / inline hex / rgba pastel / rounded-2xl
 *     blob has been eliminated.
 *
 * Material-scope preserved:
 *   • Every `data-testid` on the previous Roadmap page is preserved
 *     verbatim.
 *   • `GET /api/roadmap/latest`, `POST /api/roadmap/generate`, and
 *     `GET /api/partners` fetches + regenerate action, path/status
 *     rendering, and phase-row expand/collapse behaviour.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  FileUp,
  Handshake,
  Info,
  Loader2,
  Lock,
  RefreshCw,
  Rocket,
  Target,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import AppNav from "@/components/app/AppNav";
import CreditReportCTA from "@/components/app/CreditReportCTA";
import { Badge, Button } from "@/design-system";

const TIER_TONE = {
  funding_ready:            "success",
  almost_ready:             "accent",
  improvement_recommended:  "warning",
  repair_required:          "danger",
};

const PATH_META = {
  startup:  { icon: Rocket,     label: "Startup Funding Path" },
  business: { icon: Building2,  label: "Established Business Path" },
};

const STATUS_META = {
  completed:  { icon: CheckCircle2, label: "Completed",   tone: "success" },
  current:    { icon: Target,       label: "In progress", tone: "accent" },
  upcoming:   { icon: ChevronRight, label: "Upcoming",    tone: "neutral" },
  locked:     { icon: Lock,         label: "Locked",      tone: "neutral" },
};

export default function Roadmap() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [roadmap, setRoadmap] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [partnerMatches, setPartnerMatches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/site-settings")
      .then((res) => { if (!cancelled) setSiteSettings(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user?.funding_profile_complete) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    api
      .get("/roadmap/latest")
      .then((res) => { if (!cancelled) setRoadmap(res.data); })
      .catch((err) => {
        if (!cancelled && err?.response?.status !== 404) setError(formatApiError(err));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    api
      .get("/partners")
      .then((res) => { if (!cancelled) setPartnerMatches(res.data?.matches || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.funding_profile_complete]);

  async function regenerate() {
    setRegen(true);
    setError("");
    try {
      const res = await api.post("/roadmap/generate");
      setRoadmap(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setRegen(false);
    }
  }

  if (!user?.funding_profile_complete) {
    return (
      <Shell>
        <EmptyPanel
          title="Finish your profile to unlock your blueprint."
          subtitle="Every phase is tailored to your actual profile — credit, business, goals — so it needs to be complete first."
          ctaLabel="Complete profile"
          onCta={() => navigate("/profile")}
        />
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell>
        <div
          data-testid="roadmap-loading"
          className="flex items-center justify-center min-h-[40vh]"
        >
          <Loader2
            className="w-6 h-6 animate-spin"
            style={{ color: "var(--ds-color-accent)" }}
          />
        </div>
      </Shell>
    );
  }

  if (!roadmap) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <EmptyPanel
          title="No blueprint yet."
          subtitle="Run your readiness assessment first — your blueprint is built directly from it."
          ctaLabel="Go to readiness"
          onCta={() => navigate("/readiness")}
          error={error}
        />
      </Shell>
    );
  }

  const pathMeta = PATH_META[roadmap.funding_path] || PATH_META.startup;
  const PathIcon = pathMeta.icon;
  const tierTone = TIER_TONE[roadmap.tier_snapshot] || "accent";
  const lastUpdated = new Date(roadmap.updated_at || roadmap.created_at);
  const currentPhase = roadmap.phases.find((p) => p.index === roadmap.current_phase_index);

  return (
    <Shell disclaimer={siteSettings?.disclaimer_text}>
      {/* Formal page header */}
      <header className="pt-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
          Financial Blueprint
        </div>
        <div className="mt-2 flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 max-w-2xl">
            <h1
              data-testid="roadmap-tier-label"
              className="text-[var(--ds-color-ink)] leading-tight"
              style={{
                fontFamily: "var(--ds-font-display)",
                fontWeight: 600,
                letterSpacing: "-0.02em",
                fontSize: "clamp(2rem, 3.4vw, 2.75rem)",
              }}
            >
              {roadmap.tier_label_snapshot}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge tone={tierTone} size="md" data-testid="roadmap-score-pill">
                <span className="tabular-nums">{roadmap.score_snapshot}/100</span>
              </Badge>
              <Badge tone="neutral" size="md">
                <PathIcon className="w-3.5 h-3.5" aria-hidden="true" />
                {pathMeta.label}
              </Badge>
              <span className="text-[12px] text-[var(--ds-color-ink-subtle)]">
                Last updated {lastUpdated.toLocaleString()}
              </span>
            </div>
            <p
              data-testid="roadmap-page-subtitle"
              className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)]"
            >
              Your personalized action plan toward stronger financial readiness.
            </p>
          </div>
          <Button
            variant="primary"
            size="md"
            data-testid="roadmap-regenerate-btn"
            onClick={regenerate}
            disabled={regen}
            loading={regen}
          >
            {regen ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <RefreshCw className="w-4 h-4" aria-hidden />}
            {regen ? "Regenerating…" : "Regenerate"}
          </Button>
        </div>
        {error && (
          <p
            data-testid="roadmap-error"
            className="mt-4 text-[13px]"
            style={{ color: "var(--ds-color-danger)" }}
          >
            {error}
          </p>
        )}
      </header>

      {/* Current phase callout */}
      {currentPhase && (
        <section
          data-testid="roadmap-current-callout"
          className="rounded-[var(--ds-radius-lg)] p-6 md:p-7"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-accent)]">
            Current step · {currentPhase.index} of 5
          </div>
          <h2
            className="mt-2 text-[var(--ds-color-ink)] leading-tight"
            style={{
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontSize: "clamp(1.5rem, 2.4vw, 1.875rem)",
            }}
          >
            {currentPhase.title}
          </h2>
          <p className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-2xl">
            {currentPhase.description}
          </p>
          <ol className="mt-5 space-y-2.5">
            {currentPhase.action_items.map((item, i) => (
              <li
                key={i}
                data-testid={`roadmap-current-action-${i}`}
                className="flex gap-3 items-start text-[13px] leading-relaxed text-[var(--ds-color-ink)]"
              >
                <span
                  className="mt-0.5 w-5 h-5 rounded-[var(--ds-radius-pill)] flex-shrink-0 flex items-center justify-center"
                  style={{
                    background: "var(--ds-color-accent-soft)",
                    color: "var(--ds-color-accent)",
                  }}
                >
                  <span className="text-[11px] font-semibold tabular-nums">{i + 1}</span>
                </span>
                {item}
              </li>
            ))}
          </ol>
          <div className="mt-5 inline-flex items-center gap-1.5">
            <Badge tone="neutral" size="md">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" />
              {currentPhase.estimated_duration}
            </Badge>
          </div>
        </section>
      )}

      {/* Timeline */}
      <section data-testid="roadmap-timeline" className="space-y-4">
        <SectionEyebrow>Five-step plan</SectionEyebrow>
        <ol className="space-y-3">
          {roadmap.phases.map((phase) => (
            <PhaseRow key={phase.index} phase={phase} />
          ))}
        </ol>
      </section>

      {/* Top blockers */}
      <NarrativeCard
        testId="roadmap-blockers"
        title="What's holding you back"
        subtitle="Hard items that disqualify most funding partners until resolved."
        icon={AlertTriangle}
        iconTone="warning"
      >
        {roadmap.top_blockers && roadmap.top_blockers.length > 0 ? (
          <ul className="mt-4 space-y-2.5 text-[14px] leading-relaxed text-[var(--ds-color-ink)]">
            {roadmap.top_blockers.map((b, i) => (
              <li key={i} className="flex gap-2.5 items-start">
                <AlertTriangle
                  className="w-3.5 h-3.5 mt-1 flex-shrink-0"
                  style={{ color: "var(--ds-color-warning)" }}
                  aria-hidden="true"
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)]">
            No critical blockers detected.
          </p>
        )}
      </NarrativeCard>

      {/* Next best actions */}
      <NarrativeCard
        testId="roadmap-next-best-actions"
        title="Next best actions"
        subtitle="The three highest-leverage moves to make right now."
        icon={Target}
        iconTone="accent"
      >
        <ol className="mt-4 space-y-3 text-[14px] leading-relaxed text-[var(--ds-color-ink)] list-decimal pl-5">
          {roadmap.next_best_actions.map((s, i) => (
            <li key={i} className="pl-1">{s}</li>
          ))}
        </ol>
      </NarrativeCard>

      {/* Estimated timeline */}
      <NarrativeCard
        testId="roadmap-estimated-timeline"
        title="Estimated readiness timeline"
        subtitle="Educational projection — not a guarantee of approval or outcome."
        icon={Info}
        iconTone="accent"
      >
        <p className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink)]">
          {roadmap.estimated_timeline_text || "Re-run your readiness assessment to refresh your projection."}
        </p>
      </NarrativeCard>

      {/* Partner hints */}
      <section data-testid="roadmap-partner-hints" className="space-y-4">
        <SectionEyebrow>Partner suggestions</SectionEyebrow>
        {partnerMatches && partnerMatches.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-3">
            {partnerMatches.slice(0, 4).map((p, i) => (
              <PartnerHint key={p.partner_id} partner={p} index={i} />
            ))}
          </div>
        ) : (
          <p className="text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)]">
            We&rsquo;ll match you with partners as soon as the match engine has data. Strengthen your
            profile in the meantime — every milestone above expands the partner pool.
          </p>
        )}
      </section>

      {/* Bottom CTAs */}
      <section className="grid md:grid-cols-2 gap-3">
        <BottomCtaCard
          to="/credit-report"
          testId="roadmap-cta-upload-report"
          Icon={FileUp}
          title="Upload Credit Report"
          description="Override your self-reported payment history with the accounts and negatives detected in your report."
        />
        <BottomCtaCard
          to="/partners"
          testId="roadmap-cta-partners"
          Icon={Handshake}
          title="View Partner Match"
          description="See your full partner ranking based on your profile, not static hints."
        />
      </section>
    </Shell>
  );
}

/* --------------------- Sub-components ---------------------------------- */

function PhaseRow({ phase }) {
  const [open, setOpen] = useState(phase.status === "current");
  const meta = STATUS_META[phase.status] || STATUS_META.upcoming;
  const StatusIcon = meta.icon;
  const locked = phase.status === "locked";
  return (
    <li
      data-testid={`roadmap-phase-${phase.index}`}
      data-status={phase.status}
      className="rounded-[var(--ds-radius-lg)]"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
        opacity: locked ? 0.75 : 1,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`roadmap-phase-${phase.index}-toggle`}
        className="w-full px-5 md:px-6 py-5 flex items-center gap-4 text-left"
      >
        <div
          className="flex-shrink-0 w-9 h-9 rounded-[var(--ds-radius-md)] flex items-center justify-center"
          style={{
            background:
              meta.tone === "success"
                ? "var(--ds-color-success-soft)"
                : meta.tone === "accent"
                ? "var(--ds-color-accent-soft)"
                : "var(--ds-color-surface-muted)",
            color:
              meta.tone === "success"
                ? "var(--ds-color-success)"
                : meta.tone === "accent"
                ? "var(--ds-color-accent)"
                : "var(--ds-color-ink-muted)",
          }}
        >
          <StatusIcon className="w-4 h-4" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
              Step {phase.index}
            </span>
            <Badge tone={meta.tone === "neutral" ? "neutral" : meta.tone}>
              {meta.label}
            </Badge>
          </div>
          <div
            className="mt-1 text-[var(--ds-color-ink)]"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.0625rem", lineHeight: 1.35 }}
          >
            {phase.title}
          </div>
        </div>
        <div className="hidden md:block text-[12px] text-[var(--ds-color-ink-muted)] flex-shrink-0">
          {phase.estimated_duration}
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 flex-shrink-0 text-[var(--ds-color-ink-muted)]" aria-hidden />
        ) : (
          <ChevronRight className="w-4 h-4 flex-shrink-0 text-[var(--ds-color-ink-muted)]" aria-hidden />
        )}
      </button>
      {open && (
        <div
          className="px-5 md:px-6 pb-5 border-t"
          style={{ borderColor: "var(--ds-color-border)" }}
        >
          <p className="mt-4 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)]">{phase.description}</p>
          <ul className="mt-4 space-y-2.5 text-[13px] leading-relaxed text-[var(--ds-color-ink)]">
            {phase.action_items.map((item, i) => (
              <li key={i} className="flex gap-2.5 items-start">
                <span
                  className="mt-2 w-1.5 h-1.5 rounded-[var(--ds-radius-pill)] flex-shrink-0"
                  style={{ background: "var(--ds-color-accent)" }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {(phase.index === 1 || /credit/i.test(phase.title || "")) && (
            <div className="mt-5">
              <CreditReportCTA testid={`roadmap-phase-${phase.index}-credit-cta`} />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function PartnerHint({ partner, index }) {
  const statusTone =
    partner.status === "eligible"
      ? "success"
      : partner.status === "almost_eligible"
      ? "accent"
      : "neutral";
  const statusLabel =
    partner.status === "eligible"
      ? "Eligible"
      : partner.status === "almost_eligible"
      ? "Almost"
      : "Not yet";
  return (
    <Link
      to="/partners"
      data-testid={`roadmap-partner-hint-${index}`}
      className="block rounded-[var(--ds-radius-lg)] p-5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
        transitionDuration: "var(--ds-duration-fast)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border-strong)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border)")}
    >
      <div className="flex items-start gap-3">
        <div
          className="inline-flex items-center justify-center w-9 h-9 flex-shrink-0 rounded-[var(--ds-radius-md)]"
          style={{ background: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)" }}
        >
          <Handshake className="w-4 h-4" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="text-[var(--ds-color-ink)]"
              style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
            >
              {partner.partner_name}
            </div>
            <Badge tone={statusTone} data-testid={`roadmap-partner-hint-${index}-status`}>
              {statusLabel}
            </Badge>
          </div>
          {partner.funding_range_label && (
            <p className="mt-1 text-[12px] text-[var(--ds-color-ink-muted)]">
              {partner.funding_range_label}
            </p>
          )}
          <p className="mt-2 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)] line-clamp-2">
            {partner.description}
          </p>
          <span
            className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: "var(--ds-color-accent)" }}
          >
            View full match details <ArrowRight className="w-3 h-3" aria-hidden />
          </span>
        </div>
      </div>
    </Link>
  );
}

function BottomCtaCard({ to, testId, Icon, title, description }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="block rounded-[var(--ds-radius-lg)] p-6 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
        transitionDuration: "var(--ds-duration-fast)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border-strong)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border)")}
    >
      <div className="flex items-start gap-3">
        <Icon
          className="w-5 h-5 mt-1 flex-shrink-0"
          style={{ color: "var(--ds-color-accent)" }}
          aria-hidden="true"
        />
        <div>
          <div
            className="text-[var(--ds-color-ink)]"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
          >
            {title}
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
            {description}
          </p>
        </div>
      </div>
    </Link>
  );
}

function Shell({ children, disclaimer }) {
  return (
    <div
      data-testid="roadmap-root"
      data-page-outer="wave-2"
      className="min-h-screen bg-[var(--ds-color-background)] text-[var(--ds-color-ink)]"
    >
      <AppNav />
      <main className="max-w-5xl mx-auto px-4 md:px-8 pt-10 pb-20 space-y-10">
        {disclaimer && (
          <div
            data-testid="roadmap-disclaimer"
            className="rounded-[var(--ds-radius-md)] p-4 flex gap-3"
            style={{
              background: "var(--ds-color-surface-muted)",
              border: "1px solid var(--ds-color-border)",
            }}
          >
            <Info
              className="w-4 h-4 mt-0.5 flex-shrink-0"
              style={{ color: "var(--ds-color-ink-muted)" }}
              aria-hidden="true"
            />
            <p className="text-[12px] leading-relaxed text-[var(--ds-color-ink-muted)]">{disclaimer}</p>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
      {children}
    </div>
  );
}

function NarrativeCard({ testId, title, subtitle, icon: Icon, iconTone = "accent", children }) {
  const chipBg =
    iconTone === "success"
      ? "var(--ds-color-success-soft)"
      : iconTone === "warning"
      ? "var(--ds-color-warning-soft)"
      : iconTone === "danger"
      ? "var(--ds-color-danger-soft)"
      : "var(--ds-color-accent-soft)";
  const chipFg =
    iconTone === "success"
      ? "var(--ds-color-success)"
      : iconTone === "warning"
      ? "var(--ds-color-warning)"
      : iconTone === "danger"
      ? "var(--ds-color-danger)"
      : "var(--ds-color-accent)";
  return (
    <section
      data-testid={testId}
      className="rounded-[var(--ds-radius-lg)] p-6 md:p-7"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="inline-flex items-center justify-center w-9 h-9 rounded-[var(--ds-radius-md)] flex-shrink-0"
          style={{ background: chipBg, color: chipFg }}
        >
          <Icon className="w-4 h-4" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="text-[var(--ds-color-ink)]"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.0625rem" }}
          >
            {title}
          </h3>
          {subtitle && (
            <p className="mt-1 text-[12px] leading-relaxed text-[var(--ds-color-ink-muted)]">{subtitle}</p>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

function EmptyPanel({ title, subtitle, ctaLabel, onCta, error }) {
  return (
    <section
      data-testid="roadmap-empty"
      className="rounded-[var(--ds-radius-lg)] p-10 text-center"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <h2
        className="text-[var(--ds-color-ink)]"
        style={{
          fontFamily: "var(--ds-font-display)",
          fontWeight: 600,
          letterSpacing: "-0.02em",
          fontSize: "clamp(1.5rem, 2.4vw, 1.875rem)",
        }}
      >
        {title}
      </h2>
      <p className="mt-3 text-[14px] max-w-xl mx-auto leading-relaxed text-[var(--ds-color-ink-muted)]">
        {subtitle}
      </p>
      {error && (
        <p className="mt-3 text-[13px]" style={{ color: "var(--ds-color-danger)" }}>
          {error}
        </p>
      )}
      <div className="mt-6 flex justify-center">
        <Button
          variant="primary"
          size="md"
          data-testid="roadmap-empty-cta-btn"
          onClick={onCta}
        >
          {ctaLabel} <ArrowRight className="w-4 h-4" aria-hidden />
        </Button>
      </div>
    </section>
  );
}
